<template>
  <div>
    <csm-banner />
    <checkout-header />
    <div class="container" v-if="!PRODUCTS.length">
      <page-info
        class="offset-page-info"
        title="Корзина пуста"
        subtitle="В корзину ничего не добавлено. Чтобы добавить товары перейдите в каталог"
      >
        <ui-button text="Начать покупки" @click="goHome" />
      </page-info>
    </div>

    <template v-else>
      <div class="container checkout-page">
        <div class="checkout-content">
          <h1 class="checkout-page__title">
            Оформление заказа
          </h1>
          <vue-client-only>
            <checkout-delivery-widget
              v-if="adaptive.isMobile"
              class="checkout-page__delivery-widget"
            />
          </vue-client-only>
          <checkout-delivery
            v-if="isProductsUnallocated"
            ref="delivery"
          />
          <checkout-products :cart-is-loading="CART_IS_LOADING" />
          <checkout-contact ref="contact" />
          <checkout-comment label="" class="checkout-page__comment" />
          <checkout-payment ref="payment" />
          <vue-client-only>
            <checkout-total
              v-if="!adaptive.isTablet"
              :loading="submitProgress"
              @submit="submit(false)"
            />
          </vue-client-only>
        </div>

        <aside>
          <vue-client-only>
            <div v-if="!adaptive.isMobile" class="checkout-page__aside-content">
              <checkout-delivery-widget
                :adaptive="false"
                class="checkout-page__delivery-widget"
              />
              <checkout-total-widget @submit="submit(true)" />
            </div>
          </vue-client-only>
        </aside>

        <delivery-selector />
      </div>

      <vue-client-only>
        <div v-if="adaptive.isTablet" class="container checkout-page">
          <checkout-total
            :loading="submitProgress"
            @submit="submit(false)"
          />
        </div>
      </vue-client-only>
    </template>

    <div class="container">
      <product-gallery-recomendations
        class="checkout-page__recomendations"
        title="Популярные товары"
        :product-ids="cartProductsIds"
        v-if="PRODUCTS.length"
      />
      <product-gallery-slot
        v-if="!PRODUCTS.length"
        :with-category="false"
        slot-name="popular_main"
        class="offset-recommendation-gallery"
      />
    </div>

    <ui-loader
      v-if="isLoading || CART_IS_LOADING"
      size="64"
      class="checkout-loader"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import VueClientOnly from 'vue-client-only';

/* Components */
import PageInfo from '../components/common/page-info.vue';
import CheckoutHeader from '../components/checkout/checkout-header.vue';
import CheckoutProducts from '../components/checkout/checkout-products.vue';
import CheckoutDelivery from '../components/checkout/checkout-delivery.vue';
import CheckoutContact from '@/components/checkout/checkout-contact/checkout-contact.vue';
import CheckoutPayment from '../components/checkout/checkout-payment/checkout-payment.vue';
import CheckoutTotal from '@/components/checkout/checkout-total/checkout-total.vue';
import DeliverySelector from '@/components/checkout/delivery-selector/delivery-selector.vue';
import ProductGalleryRecomendations from '@/components/product-gallery-recomendations/product-gallery-recomendations.vue';
import ProductGallerySlot from '@/components/product-gallery-slot/product-gallery-slot.vue';
import CsmBanner from '@/components/header/csm-banner.vue';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';
import pageMetaMixin from '@/mixins/page-meta-mixin';
import CheckoutComment from '@/components/checkout/checkout-comment.vue';
import CheckoutTotalWidget from '@/components/checkout/checkout-total-widget/checkout-total-widget.vue';
import CheckoutDeliveryWidget from '@/components/checkout/checkout-delivery-widget/checkout-delivery-widget.vue';

const LOCAL_ERRORS = {
  deliveryNotDefined: 'delivery_not_defined',
  paymentNotDefined: 'payment_not_defined',
};

export default {
  name: 'checkout-page',

  metaInfo() {
    return this.getPageMeta({ pageTitle: 'Оформление заказа' });
  },

  components: {
    VueClientOnly,
    CheckoutDeliveryWidget,
    CheckoutComment,
    CheckoutTotalWidget,
    CsmBanner,
    PageInfo,
    CheckoutHeader,
    CheckoutProducts,
    CheckoutDelivery,
    CheckoutContact,
    CheckoutPayment,
    CheckoutTotal,
    DeliverySelector,
    ProductGallerySlot,
    ProductGalleryRecomendations,
  },

  mixins: [pageMetaMixin],

  inject: ['adaptive'],

  data: () => ({
    isMounted: false, // прикрывает двойное срабатывание watch при загрузке страницы
    // вызванное костылем в checkout-payment.vue (setPaymentMethod)
    submitProgress: false,
    isLoading: false,
    promoCode: '',
    eventLabelValue: 1
  }),

  computed: {
    ...mapState('cart', ['cartId', 'paymentMethods']),
    ...mapGetters({
      PRODUCTS: 'cart/products',
      SHIPPING_GROUPS: 'cart/shippingGroups',
      ACCOUNT: 'user/account',
      USER_CITY: 'user/city',
      PROMO_CODES: 'cart/promoCodes',
      CART_IS_LOADING: 'cart/isLoading',
      DELIVERY_INFO: 'cart/deliveryInfo',
    }),

    cartProductsIds() {
      return this.PRODUCTS.map(i => i.id);
    },

    isDelivery() {
      return this.SHIPPING_GROUPS.some(g => g.type === 'delivery');
    },

    isProductsUnallocated() {
      return this.SHIPPING_GROUPS.some(g => g.type === 'unallocated') && this.SHIPPING_GROUPS.length <= 1;
    },

    isValidAddress() {
      return !!this.DELIVERY_INFO?.address?.streetName;
    },
  },

  watch: {
    async USER_CITY() {
      this.isLoading = true;
      this.promoCode = this.PROMO_CODES?.[0]?.code; // после обновления корзины код в сторе удалится

      try {
        await this.LOAD_CART();

        if (!this.promoCode) return;
        await this.SET_PROMO(this.promoCode);
      } catch (e) {
        let message = e.response?.config?.url.includes('promo')
            ? 'Промокод не найден' : e.response.data;
        this.$store.dispatch('notifications/create', {
          title: 'Ошибка',
          message
        });
      } finally {
        this.isLoading = false;
        this.$f.$emit('cart-city-changed');
      }
    },

    SHIPPING_GROUPS(groups) {
      if (groups.length > 1 && this.isMounted) this.calculateEventLabelValue();
    },

    eventLabelValue(value) {
      cartGtmEvents.notChooseDeliveryType({ value });
    },
  },

  async mounted() {
    // побеждаем браузерный кэш
    await this.LOAD_CART();
    await this.ENABLE_USE_BONUSES();

    // Google Tag Manager event
    cartGtmEvents.viewCartPage({
      cartId: this.cartId,
      products: this.PRODUCTS,
    });

    if (this.SHIPPING_GROUPS.length > 1) {
      this.calculateEventLabelValue();
    }
    this.isMounted = true;
  },

  methods: {
    ...mapActions({
      LOAD_CART: 'cart/loadCart',
      ENABLE_USE_BONUSES: 'cart/enableUseBonuses',
      CART_SUBMIT: 'cart/submit',
      SET_PROMO: 'cart/setPromo',
    }),

    goHome() {
      window.location = '/';
    },

    handleLocalErrors() {
      return new Promise((resolve, reject) => {
        if (
          (this.isDelivery && !this.isValidAddress)
          || this.SHIPPING_GROUPS.some(group => ['unallocated'].includes(group.type))
        ) {
          reject({
            title: 'Не выбран способ доставки',
            message: 'Где и как вы хотите получить заказ?',
            error: LOCAL_ERRORS.deliveryNotDefined,
          });
        }
        if (!this.paymentMethods.some(method => method.selected)) {
          reject({
            title: 'Не выбран способ оплаты',
            message: 'Как вы хотите оплатить заказ?',
            error: LOCAL_ERRORS.paymentNotDefined,
          });
        }
        if (this.SHIPPING_GROUPS.some(group => ['soldout'].includes(group.type))) {
          reject({
            title: 'Товар распродан',
            message: 'Удалите товар из корзины'
          });
        }
        resolve();
      });
    },

    async validateForms() {
      const { contact } = this.$refs;
      const validationPromises = [contact]
        .filter(ref => !!ref)
        .map(ref => ref.checkFormValidity());
      const validationsResults = await Promise.all(validationPromises);

     return validationsResults.every(isValid => isValid === true);
    },

    scrollToError() {
      this.$nextTick(() => {
        const error = document.querySelector('.ui-input.error');

        if(!error) return;

        this.scrollIntoView(error);
      })
    },

    scrollIntoView(element) {
      if (!element) return;

      const $el = typeof element === 'string'
        ? this.$el?.querySelector(element)
        : element._isVue ? element.$el : element;

      if (typeof $el.scrollIntoView !== 'function') return;

      $el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },

    async submit(isFromWidget = false) {
      if (!await this.validateForms()) {
        this.$store.dispatch('notifications/create', {
          title: 'Ошибка',
          message: 'Не заполнены обязательные поля',
        });

        this.scrollToError();

        return;
      }

      try {
        this.submitProgress = true;
        await this.handleLocalErrors();

        if (isFromWidget) {
          this.scrollIntoView('.checkout-total__submit');
        } else {
          await this.CART_SUBMIT();
          await cartGtmEvents.submitOrder();
        }
      } catch (e) {
        const { title, message, error } = { title: 'Ошибка', message: '', error: {}, ...e };

        if (message || error.code && error.code === 'error.cart_invalid') {
          this.$store.dispatch('notifications/create', {
            title,
            message: message || 'Не заполнены обязательные поля',
          });

          if (error === LOCAL_ERRORS.deliveryNotDefined) {
            this.scrollIntoView(this.$refs.delivery);
            cartGtmEvents.notChooseDeliveryType();
          }

          if (error === LOCAL_ERRORS.paymentNotDefined) {
            this.scrollIntoView(this.$refs.payment);
          }
        }
      } finally {
        this.submitProgress = false;
      }
    },

    calculateEventLabelValue() {
      this.eventLabelValue =
          this.SHIPPING_GROUPS.some(g => g.type === 'unallocated' || g.type === 'soldout') ? 0.5 : 0;
      this.eventLabelValue +=
          this.SHIPPING_GROUPS.reduce((acc, g) => {
            g.type === 'pickup' || g.type === 'delivery' || g.type === 'thirdparty' ? acc++ : null;
            return acc;
          }, 0)
    },
  },

  serverPrefetch () {
    return this.LOAD_CART();
  },
};
</script>

<style lang="scss">
@import "@/components/common/page-info-with-gallery.scss";

.checkout-page {
  flex-direction: column;

  aside {
    display: none;
  }

  h3 {
    font-size: 20px;
    line-height: 28px;
    margin: 32px 0 4px;
    font-weight: 300;
  }

  &__title {
    font-size: 24px;
    line-height: 32px;
    margin: 20px 0;
    font-weight: 300;
  }

  &__aside-content {
    position: sticky;
    top: 40px;
  }

  h4 {
    font-size: 14px;
    line-height: 20px;
    margin: 24px 0 6px;
    font-weight: 600;
  }

  &__comment {
    margin-top: 4px;
  }

  &__delivery-widget {
    margin-bottom: 20px;
  }
}

.checkout-loader {
  position: fixed;
  top: 50%;
  left: 50%;
  color: $blue;
  transform: translate(-50%, -50%);
}

@include breakpoint("tablet") {
  .checkout-page {
    display: flex;
    flex-direction: row;

    .checkout-content {
      flex: 1;
    }

    h3 {
      margin-top: 24px;
    }

    &__title {
      margin-top: 15px;
    }

    aside {
      display: block;
      width: 218px;
      margin-left: 24px;
      padding-top: 71px;
    }

    &__comment {
      margin-top: 12px;
    }

    &__delivery-widget {
      margin-bottom: 16px;
    }
  }
}

@include breakpoint("desktop") {
  .checkout-page {
    h3 {
      font-size: 24px;
      line-height: 32px;
      margin-top: 56px;
    }

    &__title {
      margin: 40px 0 32px;
    }

    aside {
      width: 276px;
      margin-left: 36px;
      padding: 108px 0 162px;
    }

    h4 {
      font-size: 16px;
      line-height: 24px;
      margin: 32px 0 8px;
      font-weight: 600;
    }
  }
}

.checkout-page__recomendations {
  margin-top: 32px;
  margin-bottom: 32px;

  @include breakpoint("tablet") {
    margin-top: 48px;
    margin-bottom: 48px;
  }

  @include breakpoint("desktop") {
    margin-top: 72px;
    margin-bottom: 58px;
  }
}

</style>
